import { NextSeo } from 'next-seo';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useEffect, useMemo } from 'react';
import { twJoin } from 'tailwind-merge';

import FlatLayout from 'components/Layout/FlatLayout';

import useAdBanner from 'hooks/useAdBanner';
import useIntentBanner from 'hooks/useIntentBanner';
import useModal from 'hooks/useModal';

import { stripQs } from 'lib/request';
import { sendSupixEvent } from 'lib/supix';

import FlashOffersProvider from 'providers/FlashOffersProvider';
import { useGlobalQueries } from 'providers/GlobalQueriesProvider';
import { useUtm } from 'providers/UtmProvider';

import FeaturedOffers from './FeaturedOffers';
import HomePills from './HomePills';
import OffersListSection from './OffersListSection';
import OffersTimelineSchema from './Schema';
import HomeSidebar from './Sidebar';
import WishlistMaintenanceActionSheet from './WishlistMaintenanceActionSheet';

import { INTENT_TYPES } from 'constants/intentBanner';
import LINKS from 'constants/links';

const ScrollToTopButton = dynamic(
  () => import('components/ScrollToTopButton'),
  { ssr: false }
);

const ResetPasswordModal = dynamic(
  () => import('components/ResetPasswordModal'),
  { ssr: false }
);

const HomeBannerCarousel = dynamic(() => import('./HomeBannerCarousel'), {
  ssr: true,
});

const GA_EVENT_CATEGORY = 'timeline';
const HIGHLIGHTS_OFFERS = 'highlights';

const HomePage = ({
  canonicalUrl = null,
  cardsAds,
  description = null,
  h1Text = null,
  isUserLogged = false,
  landscapeAds = [],
  pageTitle = null,
  recoveryPassword = null,
  serverCoupons = [],
  serverFeaturedOffers,
  serverFeaturedStores = [],
  serverOffers,
  serverHomePills,
  serverHomePillsTitle = null,
}) => {
  const { activeOffersListTab } = useGlobalQueries();
  const { showModal } = useModal();
  const router = useRouter();
  const { isUtmLoaded } = useUtm();
  const { isAdBannerVisible } = useAdBanner();

  // First/Exit intent banners (behavioral overlays). Self-contained: fetch on
  // demand, frequency cap, triggers and Supix 0006 events all live in the hook.
  // Gated by the presence of an active intent banner in /ads (admin-scheduled).
  useIntentBanner(INTENT_TYPES.FIRST);
  useIntentBanner(INTENT_TYPES.EXIT);

  useEffect(() => {
    if (!recoveryPassword) {
      return;
    }

    /**
     * When the query params of recovery password are invalid, we remove these
     * params from the URL without refresh completely the page and add a new
     * state in navigation history
     */
    if (recoveryPassword) {
      if (recoveryPassword.isValid) {
        showModal(ResetPasswordModal, recoveryPassword);
        return;
      }

      router.replace(LINKS.HOME, undefined, { shallow: true });
    }
  }, []);

  useEffect(() => {
    if (isUtmLoaded) {
      sendSupixEvent('/car', { category: 0 });
    }
  }, [isUtmLoaded]);

  const visibleCardsAds = useMemo(
    () => cardsAds.filter(isAdBannerVisible),
    [cardsAds, isAdBannerVisible]
  );

  // Normalise HOME_AD entries: ops may only upload a single image — fall back to
  // the desktop image so AdCarouselItem doesn't render broken img tags on mobile.
  const processedLandscapeAds = useMemo(
    () =>
      landscapeAds.filter(isAdBannerVisible).map((ad) => ({
        ...ad,
        mobileImage: ad.mobileImage || ad.image,
        tabletImage: ad.tabletImage || ad.image,
      })),
    [landscapeAds, isAdBannerVisible]
  );

  const isIndexed = stripQs(router.asPath) !== LINKS.WISHLIST;

  return (
    <>
      <NextSeo
        description={description}
        nofollow={!isIndexed}
        noindex={!isIndexed}
        openGraph={{
          title: pageTitle,
          description,
        }}
        title={pageTitle}
      />
      {canonicalUrl && isIndexed && serverOffers?.offers?.length > 0 && (
        <OffersTimelineSchema
          canonicalUrl={canonicalUrl}
          name={pageTitle}
          offers={serverOffers.offers}
        />
      )}
      <FlashOffersProvider>
        <FlatLayout.Margin className="-mt-4 lg:-mt-14 [&_*]:font-rubik">
          <FlatLayout.Body>
            {processedLandscapeAds.length > 0 && (
              <div className="mb-6 mt-4 hidden lg:block">
                <HomeBannerCarousel
                  ads={processedLandscapeAds}
                  gaEventCategory={GA_EVENT_CATEGORY}
                  height={140}
                  width={1330}
                />
              </div>
            )}
            {processedLandscapeAds.length > 0 && (
              <div className="mb-4 mt-4 lg:hidden">
                <HomeBannerCarousel
                  ads={processedLandscapeAds}
                  gaEventCategory={GA_EVENT_CATEGORY}
                  height={128}
                  width={377}
                />
              </div>
            )}
            <HomePills stores={serverHomePills} title={serverHomePillsTitle} />
            <div
              className="min-[83.125rem]:flex min-[83.125rem]:min-h-0 min-[83.125rem]:items-start min-[83.125rem]:gap-x-6"
              data-page="home"
            >
              <div className="min-[83.125rem]:min-w-0 min-[83.125rem]:flex-1">
                <FeaturedOffers
                  className={twJoin(
                    'mb-4 lg:mb-0',
                    activeOffersListTab.id !== HIGHLIGHTS_OFFERS &&
                      'hidden md:block'
                  )}
                  gaEventCategory={GA_EVENT_CATEGORY}
                  serverFeaturedOffers={serverFeaturedOffers}
                />
                <div className="mt-12 grid grid-cols-4 gap-0 gap-x-4 md:grid-cols-12 lg:mt-6">
                  <OffersListSection
                    cardsAds={cardsAds}
                    gaEventCategory={GA_EVENT_CATEGORY}
                    h1Text={h1Text}
                    serverOffers={serverOffers}
                  />
                </div>
              </div>
              <aside className="hidden min-[83.125rem]:block min-[83.125rem]:w-[280px] min-[83.125rem]:shrink-0 min-[83.125rem]:self-start">
                <div className="flex flex-col gap-y-6">
                  <HomeSidebar
                    isUserLogged={isUserLogged}
                    serverCoupons={serverCoupons}
                    serverFeaturedStores={serverFeaturedStores}
                    sidebarAds={visibleCardsAds}
                  />
                </div>
              </aside>
            </div>
            <ScrollToTopButton gaEventCategory={GA_EVENT_CATEGORY} />
          </FlatLayout.Body>
        </FlatLayout.Margin>
        <FlatLayout.Footer />
        <WishlistMaintenanceActionSheet />
      </FlashOffersProvider>
    </>
  );
};

HomePage.propTypes = {
  canonicalUrl: PropTypes.string,
  cardsAds: PropTypes.arrayOf(
    PropTypes.shape({
      bgcolor: PropTypes.arrayOf(PropTypes.string),
      description: PropTypes.string.isRequired,
      id: PropTypes.number.isRequired,
      image: PropTypes.string.isRequired,
      isClosable: PropTypes.bool.isRequired,
      mobileImage: PropTypes.string.isRequired,
      position: PropTypes.number.isRequired,
      storeSlug: PropTypes.string,
      type: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
    })
  ).isRequired,
  description: PropTypes.string,
  h1Text: PropTypes.string,
  isUserLogged: PropTypes.bool,
  landscapeAds: PropTypes.arrayOf(
    PropTypes.shape({
      description: PropTypes.string.isRequired,
      id: PropTypes.number.isRequired,
      image: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
      visibility: PropTypes.string.isRequired,
    })
  ),
  recoveryPassword: PropTypes.shape({
    recoveryHash: PropTypes.string.isRequired,
    userEmail: PropTypes.string.isRequired,
    isValid: PropTypes.bool.isRequired,
  }),
  serverFeaturedOffers: PropTypes.arrayOf(
    PropTypes.shape({
      categoryId: PropTypes.number.isRequired,
      categorySlug: PropTypes.string.isRequired,
      key: PropTypes.string.isRequired,
      lastActiveOffer: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
      offerComments: PropTypes.number.isRequired,
      offerId: PropTypes.number.isRequired,
      offerIsHighlight: PropTypes.bool.isRequired,
      ratings: PropTypes.shape().isRequired,
      offerOldPrice: PropTypes.number,
      offerPhoto: PropTypes.string.isRequired,
      offerPrice: PropTypes.number.isRequired,
      offerPriceType: PropTypes.string.isRequired,
      offerPublished: PropTypes.string.isRequired,
      offerSlug: PropTypes.string.isRequired,
      offerStatusName: PropTypes.string.isRequired,
      offerUserVisibility: PropTypes.string.isRequired,
      offerTags: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string.isRequired,
          type: PropTypes.string.isRequired,
        })
      ),
      offerTitle: PropTypes.string.isRequired,
      pageTitle: PropTypes.string,
      storeDomain: PropTypes.string.isRequired,
      storeId: PropTypes.number,
      storeName: PropTypes.string,
      subcategoryId: PropTypes.number,
      subcategorySlug: PropTypes.string.isRequired,
      userId: PropTypes.number.isRequired,
      userLevel: PropTypes.number,
      userName: PropTypes.string.isRequired,
      userPhoto: PropTypes.string.isRequired,
      userTypeName: PropTypes.string.isRequired,
      userUsername: PropTypes.string.isRequired,
    })
  ).isRequired,
  serverOffers: PropTypes.shape({
    after: PropTypes.string.isRequired,
    before: PropTypes.number,
    offers: PropTypes.arrayOf(
      PropTypes.shape({
        categoryId: PropTypes.number,
        categoryName: PropTypes.string,
        categorySlug: PropTypes.string,
        key: PropTypes.string.isRequired,
        offerComments: PropTypes.number.isRequired,
        offerId: PropTypes.number.isRequired,
        offerIsHighlight: PropTypes.bool.isRequired,
        ratings: PropTypes.shape().isRequired,
        offerOldPrice: PropTypes.number,
        offerPhoto: PropTypes.string.isRequired,
        offerPrice: PropTypes.number.isRequired,
        offerPriceType: PropTypes.string.isRequired,
        offerPublished: PropTypes.string.isRequired,
        offerSlug: PropTypes.string.isRequired,
        offerStatusName: PropTypes.string.isRequired,
        offerUserVisibility: PropTypes.string.isRequired,
        offerTags: PropTypes.arrayOf(
          PropTypes.shape({
            name: PropTypes.string.isRequired,
            type: PropTypes.string.isRequired,
          })
        ),
        offerTitle: PropTypes.string.isRequired,
        storeDomain: PropTypes.string.isRequired,
        storeId: PropTypes.number,
        storeImage: PropTypes.string,
        storeName: PropTypes.string,
        subcategoryId: PropTypes.number,
        subcategoryName: PropTypes.string,
        subcategorySlug: PropTypes.string,
        userId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
          .isRequired,
        userName: PropTypes.string.isRequired,
        userPhoto: PropTypes.string.isRequired,
        userTypeName: PropTypes.string,
        userUsername: PropTypes.string.isRequired,
      })
    ),
  }),
};

export default HomePage;
